import { useQuery } from 'react-query';
import { useTokenizedApi } from '../useTokenizedApi';
import { usePersonalProfile } from '../entities/usePersonalProfile';
import { usePrimaryLockbox } from '../treasury/usePrimaryLockbox';

/**
 * **`React Query hook`** Gets the details of a specific activity event. While the hook
 * is mounted, the activity event will be refreshed automatically **every 60 seconds**.
 * Use this to show details for one specific user event.
 *
 * See https://react-query.tanstack.com/guides/queries for general information
 * about using a React Query `UseQueryResults` object.
 *
 * @param {string} id - The ID of the event to retrieve
 * @returns An object describing the event, as
 *   the `data` property within a React Query
 *   `UseQueryResult` object, or `null`
 *   if the event can’t be found.
 *
 * @category Entities and Profiles
 */

export function useActivityId({
  profileId,
  lockboxId,
  activityId,
  dateModified,
}) {
  const tokenizedApi = useTokenizedApi();

  const personalProfile = usePersonalProfile();
  profileId = profileId || personalProfile?.id;

  const primaryLockbox = usePrimaryLockbox(profileId);
  lockboxId = lockboxId || primaryLockbox?.id;

  const specificEventQueryResult = useQuery(
    tokenizedApi.activity.activityEventQuery({
      profileId,
      lockboxId,
      activityId,
      dateModified,
    }),
  );

  if (!specificEventQueryResult.data && profileId && activityId) {
    // Use latest available data from cache as fallback
    // while the latest query is loading
    const olderDataFromCache =
      tokenizedApi.activity.getLatestFetchedActivityEvent({
        profileId,
        lockboxId,
        activityId,
      });
    if (olderDataFromCache) {
      return {
        ...specificEventQueryResult,
        data: olderDataFromCache,
        isLoading: false,
      };
    }
  }

  return specificEventQueryResult;
}
